import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  devicesAPI,
  companiesAPI,
} from '../services/api';
import { useAuthStore } from '../store/authStore';
import './DistribuidorDashboard.css';

export default function DistribuidorDashboard() {
  const user = useAuthStore((state) => state.user);
  const userCompanyId = user?.company_id;

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  const [metrics, setMetrics] = useState({
    totalBoxes: 0,
    boxesAssigned: 0,
    boxesUnassigned: 0,
    percentageAssigned: 0,
  });

  const [company, setCompany] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load initial dashboard data once on mount (no auto-refresh)
    fetchInitialData();
  }, []);

  useEffect(() => {
    calculateMetrics();
  }, [devices, filters]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [devicesRes, companiesRes] = await Promise.all([
        devicesAPI.getPublic(),
        companiesAPI.getPublic(),
      ]);

      const devicesList = devicesRes.data || [];
      const companiesList = companiesRes.data || [];

      // Filter devices to only show those in user's company
      const filteredDevices = devicesList.filter(d => d.company_id === userCompanyId);

      setDevices(filteredDevices);

      // Get company info
      const userCompany = companiesList.find(c => c.id === userCompanyId);
      setCompany(userCompany);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    let dateFilteredDevices = [...devices];
    
    // Apply date filters
    if (filters.startDate) {
      dateFilteredDevices = dateFilteredDevices.filter(d => {
        const createdDate = new Date(d.created_at || d.created_date);
        return createdDate >= new Date(filters.startDate);
      });
    }
    if (filters.endDate) {
      dateFilteredDevices = dateFilteredDevices.filter(d => {
        const createdDate = new Date(d.created_at || d.created_date);
        return createdDate <= new Date(filters.endDate);
      });
    }

    // Count unique boxes
    const uniqueBoxes = new Set(dateFilteredDevices.filter(d => d.box).map(d => d.box));
    const totalBoxes = uniqueBoxes.size;

    // Count boxes with assignments (have depot_id through device assignment)
    const boxesWithAssignment = new Set();
    dateFilteredDevices.forEach(d => {
      if (d.box && (d.depot_id || d.box_assignment_depot_id)) {
        boxesWithAssignment.add(d.box);
      }
    });
    const boxesAssigned = boxesWithAssignment.size;
    const boxesUnassigned = totalBoxes - boxesAssigned;
    const percentageAssigned = totalBoxes > 0 ? Math.round((boxesAssigned / totalBoxes) * 100) : 0;

    setMetrics({
      totalBoxes,
      boxesAssigned,
      boxesUnassigned,
      percentageAssigned,
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(v => v !== '').length;
  };

  if (loading) {
    return <div className="distribuidor-dashboard loading">Loading dashboard...</div>;
  }

  return (
    <div className={`distribuidor-dashboard ${showFiltersPanel ? 'panel-open' : ''}`}>
      {/* Settings Button - Outside Container */}
      <div className="dashboard-header">
        <button 
          className="settings-btn"
          onClick={() => fetchInitialData()}
          title="Refresh data"
        >
          🔄
        </button>
        <button 
          className="settings-btn"
          onClick={() => setShowFiltersPanel(!showFiltersPanel)}
          title="Open filters"
        >
          ☰
          {getActiveFilterCount() > 0 && (
            <span className="filter-badge">{getActiveFilterCount()}</span>
          )}
        </button>
      </div>

      {/* Filters Side Panel */}
      <div className={`filters-panel ${showFiltersPanel ? 'open' : ''}`}>
        <div className="filters-panel-header">
          <h2>Filters</h2>
          <button 
            className="close-btn"
            onClick={() => setShowFiltersPanel(false)}
            title="Close filters"
          >
            ✕
          </button>
        </div>
        
        <div className="filters-panel-content">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>

          <button 
            className="btn-reset-panel"
            onClick={resetFilters}
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className="container">
        {/* Company Header */}
        <div className="company-header">
          <h1>{company?.name || 'Company'}</h1>
          <div className="company-info">
            <span className="info-badge">{company?.ruc}</span>
            <span className="info-item">📍 {company?.address}</span>
            <span className="info-item">📞 {company?.phone}</span>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-cards">
          <div className="kpi-card">
            <div className="kpi-label">Total Boxes<span className="filter-note">(Filterable)</span></div>
            <div className="kpi-value">{metrics.totalBoxes.toLocaleString()}</div>
            <div className="kpi-description">Boxes in this company</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-label">Boxes Assigned</div>
            <div className="kpi-value">{metrics.boxesAssigned.toLocaleString()}</div>
            <div className="kpi-description">Assigned to depots</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-label">Boxes Unassigned</div>
            <div className="kpi-value">{metrics.boxesUnassigned.toLocaleString()}</div>
            <div className="kpi-description">Waiting for assignment</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-label">Assignment %<span className="filter-note">(Filterable)</span></div>
            <div className="kpi-value">{metrics.percentageAssigned}%</div>
            <div className="kpi-description">Boxes assigned to depots</div>
          </div>
        </div>
      </div>
    </div>
  );
}
